// src/pages/dms/FPMS.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Breadcrumbs,
    Tabs,
    Tab,
    Paper,
    useMediaQuery,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Link as MuiLink // Import Link from Material-UI for clickable breadcrumbs
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import FPMSConfig from "../../components/FPMSConfig"; // Import the new FPMSConfig
import useAuthCheck from "../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom";

// Definisikan data tab di luar komponen untuk menghindari re-render yang tidak perlu.
const tabConfigurations = [
    {
        label: "Kode Wilayah",
        dbPath: "db_config/db_fresh_pineapple/tb_kode_wilayah",
        fieldName: "nama_kode_wilayah",
        primaryKeyField: "id_kode_wilayah",
    },
    {
        label: "Kepala Wilayah",
        dbPath: "db_config/db_fresh_pineapple/tb_kepala_wilayah",
        fieldName: "nama_kepala_wilayah",
        primaryKeyField: "id_kepala_wilayah",
    },
    {
        label: "Kepala Kebun",
        dbPath: "db_config/db_fresh_pineapple/tb_kepala_kebun",
        fieldName: "nama_kepala_kebun",
        primaryKeyField: "id_kepala_kebun",
    },
    {
        label: "Kepala Kebun Plant Care",
        dbPath: "db_config/db_fresh_pineapple/tb_kepala_kebun_plant_care",
        fieldName: "nama_kepala_kebun_plant_care",
        primaryKeyField: "id_kepala_kebun_plant_care",
    },
    {
        label: "Kepala Kebun Fruit Care",
        dbPath: "db_config/db_fresh_pineapple/tb_kepala_kebun_fruit_care",
        fieldName: "nama_kepala_kebun_fruit_care",
        primaryKeyField: "id_kepala_kebun_fruit_care",
    },
    {
        label: "Jenis Bibit",
        dbPath: "db_config/db_fresh_pineapple/tb_jenis_bibit",
        fieldName: "nama_jenis_bibit",
        primaryKeyField: "id_jenis_bibit",
    },
    {
        label: "Kelas Bibit",
        dbPath: "db_config/db_fresh_pineapple/tb_kelas_bibit",
        fieldName: "nama_kelas_bibit",
        primaryKeyField: "id_kelas_bibit",
    },
    {
        label: "Varietas Bibit",
        dbPath: "db_config/db_fresh_pineapple/tb_varietas_bibit",
        fieldName: "nama_varietas_bibit",
        primaryKeyField: "id_varietas_bibit",
    },
    {
        label: "Komoditas",
        dbPath: "db_config/db_komoditas",
        fieldName: "nama_komoditas",
        primaryKeyField: "id_komoditas",
    },
    {
        label: "Status Lokasi",
        dbPath: "db_config/db_fresh_pineapple/tb_status_lokasi",
        fieldName: "nama_status_lokasi",
        primaryKeyField: "id_status_lokasi",
    },
    {
        label: "Plot",
        dbPath: "db_config/db_fresh_pineapple/tb_plot",
        fieldName: "nama_plot",
        primaryKeyField: "id_plot",
    },
];

function FPMS() {
    const { user, loading } = useAuthCheck();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();

    const { breadcrumbs } = useOutletContext();

    const [tabIndex, setTabIndex] = useState(() => {
        const storedTabIndex = localStorage.getItem('fpmsTabIndex');
        const parsedIndex = parseInt(storedTabIndex, 10);
        if (!isNaN(parsedIndex) && parsedIndex >= 0 && parsedIndex < tabConfigurations.length) {
            return parsedIndex;
        }
        return 0; // Default to the first tab
    });

    useEffect(() => {
        localStorage.setItem('fpmsTabIndex', tabIndex.toString());
    }, [tabIndex]);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const currentTabConfig = tabConfigurations[tabIndex];

    return (
        <Box sx={{ pb: isMobile ? 8 : 0 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {breadcrumbs && breadcrumbs.map((crumb, index) => (
                    crumb.path ? (
                        <MuiLink
                            key={index}
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate(crumb.path)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {crumb.label}
                        </MuiLink>
                    ) : (
                        <Typography key={index} color="text.primary">
                            {crumb.label}
                        </Typography>
                    )
                ))}
            </Breadcrumbs>

            {/* Layout */}
            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 4 }}>
                {/* Konten utama */}
                <Box sx={{ flex: 1 }}>
                    <Card>
                        <CardContent>
                            {/* Render FPMSConfig secara dinamis berdasarkan tabIndex */}
                            {currentTabConfig && (
                                <FPMSConfig
                                    title={`Daftar ${currentTabConfig.label}`}
                                    dbPath={currentTabConfig.dbPath}
                                    fieldName={currentTabConfig.fieldName}
                                    primaryKeyField={currentTabConfig.primaryKeyField}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* Sidebar desktop */}
                {!isMobile && (
                    <Paper
                        elevation={1}
                        sx={{
                            alignSelf: "flex-start",
                        }}
                    >
                        <Tabs
                            orientation="vertical"
                            value={tabIndex}
                            onChange={handleTabChange}
                            TabIndicatorProps={{
                                sx: {
                                    left: 0,
                                    right: "auto",
                                    width: "4px",
                                    bgcolor: "primary.main",
                                },
                            }}
                            sx={{
                                width: 350,
                                borderLeft: 1,
                                borderColor: "divider",
                                ".MuiTab-root": {
                                    alignItems: "flex-start",
                                    justifyContent: "flex-start",
                                    px: 2,
                                },
                            }}
                        >
                            {/* Loop melalui konfigurasi tab untuk sidebar */}
                            {tabConfigurations.map((tab, index) => (
                                <Tab key={index} label={tab.label} />
                            ))}
                        </Tabs>
                    </Paper>
                )}
            </Box>

            {/* Bottom Tabs (Mobile) */}
            {isMobile && (
                <Paper
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        borderTop: 1,
                        borderColor: "divider",
                        zIndex: 1000,
                    }}
                    elevation={3}
                >
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons
                        allowScrollButtonsMobile
                        TabIndicatorProps={{
                            sx: { bgcolor: "primary.main", height: 3 },
                        }}
                        centered={false}
                    >
                        {/* Loop melalui konfigurasi tab untuk mobile */}
                        {tabConfigurations.map((tab, index) => (
                            <Tab key={index} label={tab.label} />
                        ))}
                    </Tabs>
                </Paper>
            )}
        </Box>
    );
}

export default FPMS;
